import { useLocation, useParams } from 'react-router-dom'

import { FolderBreadCrumb } from '@modules/_shared/components/FolderBreadCrumb'
import { FormBreadCrumb } from '@modules/_shared/components/FormBreadCrumb'
import { ObservationBreadCrumb } from '@modules/_shared/components/ObservationBreadCrumb'

type ValidRoutes = 'forms' | 'plans' | 'documents' | 'observations'

type FormBreadCrumbRenderer = (formId?: string) => JSX.Element
type ObservationBreadCrumbRenderer = (observationId?: string) => JSX.Element
type FolderBreadCrumbRenderer = (projectId: string) => JSX.Element

type BreadCrumbRenderers = {
  forms: FormBreadCrumbRenderer
  observations: ObservationBreadCrumbRenderer
  plans: FolderBreadCrumbRenderer
  documents: FolderBreadCrumbRenderer
}

const breadcrumbs: BreadCrumbRenderers = {
  forms: (formId?: string) => <FormBreadCrumb formId={formId} />,
  observations: (observationId?: string) => <ObservationBreadCrumb observationId={observationId} />,
  plans: (projectId: string) => <FolderBreadCrumb projectId={projectId} />,
  documents: (projectId: string) => <FolderBreadCrumb projectId={projectId} />,
}

export const Breadcrumb = (): JSX.Element | null => {
  const { projectId, formId, observationId } = useParams<{
    projectId: string
    formId?: string
    observationId?: string
  }>()
  const { pathname } = useLocation()

  if (!projectId) return null

  const getMainRoute = (path: string, projectId: string): ValidRoutes | null => {
    const parts = path.split(`/${projectId}/`)
    if (parts.length !== 2) return null
    const mainRoute = parts[1].split('/')[0] as ValidRoutes
    return Object.keys(breadcrumbs).includes(mainRoute) ? mainRoute : null
  }

  const mainRoute = getMainRoute(pathname, projectId)

  if (!mainRoute || !(mainRoute in breadcrumbs)) {
    return null
  }

  if (mainRoute === 'forms') return breadcrumbs['forms'](formId)
  if (mainRoute === 'observations') return breadcrumbs['observations'](observationId)

  return breadcrumbs[mainRoute](projectId)
}
